import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { mod } from '@/services/utils';

/**
 * Composable for hours validation including decimal places and enforced rounding
 * @param {Object} form - The form object containing hours property
 * @returns {Object} Validation functions and computed properties
 */
export function useHoursValidation(form) {
  const {
    companySettings
  } = storeToRefs(useCompanyStore());
  const showDecimalError = computed(() => {
    if (!form.hours) return false;
    const hours = parseFloat(form.hours);
    if (isNaN(hours)) return false;
    return hours !== parseFloat(hours.toFixed(2));
  });
  const showEnforcedRoundingError = computed(() => {
    if (!form.hours) return false;
    if (!companySettings.value.enforceRounding) return false;
    const hours = parseFloat(form.hours);
    if (isNaN(hours)) return false;
    const increment = parseFloat(companySettings.value.timeEntryIncrement || 0.1);
    return mod(hours, increment) !== 0;
  });
  const getEnforcedRoundingErrorMessage = () => {
    const increment = parseFloat(companySettings.value.timeEntryIncrement || 0.1);
    return `Hours must be an increment of ${increment}`;
  };
  const hoursRules = computed(() => [() => !!form.hours || 'This field is required', () => form.hours > 0 || 'Must be greater than 0', () => !showDecimalError.value || 'Max 2 decimal places', () => !showEnforcedRoundingError.value || getEnforcedRoundingErrorMessage()]);
  const hasHoursError = computed(() => showDecimalError.value || showEnforcedRoundingError.value);
  const isHoursValid = computed(() => {
    if (!form.hours) return false;
    const hours = parseFloat(form.hours);
    if (isNaN(hours) || hours <= 0) return false;
    return !hasHoursError.value;
  });
  return {
    showDecimalError,
    showEnforcedRoundingError,
    getEnforcedRoundingErrorMessage,
    hoursRules,
    hasHoursError,
    isHoursValid
  };
}